import {useCallback, useEffect, useState} from 'react';

import {isProduction} from '@/utils/environment';
import logError from '@/utils/errors/logError';

interface RiskConfiguration {
  scriptUrl: string;
  scriptIntegrity: string;
}

interface RiskInstance {
  publishRiskData(): Promise<string>;
}

interface RiskStatic {
  create(publicKey: string): Promise<RiskInstance | null>;
}

declare global {
  interface Window {
    Risk?: RiskStatic;
  }
}

const riskProdConfiguration: RiskConfiguration = {
  scriptUrl: 'https://risk.checkout.com/cdn/risk/2.3/risk.js',
  scriptIntegrity:
    'sha384-5MxwBLDYNCSe1Hu+REgSSz12/VIydFkxhDdiodk/dc+QMD3hr1dmn+fbHOZ4g9cB',
};

const riskSandboxConfiguration: RiskConfiguration = {
  scriptUrl: 'https://risk.sandbox.checkout.com/cdn/risk/2.3/risk.js',
  scriptIntegrity:
    'sha384-ZGdiIppkJzwran7Bjk0sUZy5z1mZGpR/MJx7LC0xCTyFE2sBpPFeLu4r15yGVei6',
};

const riskId = 'risk-js';
const customAttributeLoaded = 'data-loaded';

const useRiskScriptV2 = (publicKey: string) => {
  const [deviceSessionId, setDeviceSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const riskConfiguration: RiskConfiguration = isProduction
    ? riskProdConfiguration
    : riskSandboxConfiguration;

  const getExistingScript = useCallback(() => {
    return document.getElementById(riskId) as HTMLScriptElement | null;
  }, []);

  const createElementScript = useCallback(
    (
      resolve: (value: void | PromiseLike<void>) => void,
      reject: (reason?: any) => void,
    ): HTMLScriptElement => {
      const script = document.createElement('script');
      script.id = riskId;
      script.defer = true;
      script.type = 'text/javascript';
      script.crossOrigin = 'anonymous';
      script.integrity = riskConfiguration.scriptIntegrity;
      script.src = riskConfiguration.scriptUrl;

      script.onload = () => {
        script.setAttribute(customAttributeLoaded, 'true');
        resolve();
      };
      script.onerror = e => {
        logError(new Error('useRisk Script OnError'), {
          customTitle: 'Risk - Error loading script',
          fileName: 'useRiskV2.ts',
          functionName: 'createElementScript - script.onError',
          data: {
            errorEvent: e,
            scriptSrc: script.src,
          },
        });
        reject(new Error('Failed to load the Risk script'));
      };

      return script;
    },
    [riskConfiguration.scriptIntegrity, riskConfiguration.scriptUrl],
  );

  const loadScript = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      const existingScript = getExistingScript();
      if (existingScript) {
        if (existingScript.getAttribute(customAttributeLoaded) === 'true') {
          resolve();
        } else {
          existingScript.addEventListener('load', () => resolve());
          existingScript.addEventListener('error', e => reject(e));
        }
        return;
      }

      const script = createElementScript(resolve, reject);

      document.body.appendChild(script);
    });
  }, [createElementScript, getExistingScript]);

  const initializeRisk = useCallback(async () => {
    let errorTitle = 'Risk - Initialization error';
    try {
      await loadScript();

      if (!window.Risk) {
        errorTitle = 'Risk - SDK is not loaded';
        throw new Error(errorTitle);
      }

      const risk = await window.Risk.create(publicKey);

      if (!risk) {
        errorTitle = 'Risk - Failed to create instance';
        throw new Error(errorTitle);
      }

      const deviceSessionId = await risk.publishRiskData();
      if (!deviceSessionId) {
        errorTitle = 'Risk - Failed to get device session ID';
        throw new Error(errorTitle);
      }
      setDeviceSessionId(deviceSessionId);
    } catch (err) {
      logError(err, {
        fileName: 'useRiskV2.ts',
        functionName: 'initializeRisk',
        customTitle: errorTitle,
      });
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [loadScript, publicKey]);

  useEffect(() => {
    if (!riskConfiguration.scriptUrl || !publicKey) return;

    initializeRisk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {deviceSessionId, isLoading, error};
};

export default useRiskScriptV2;
